import { Collapse, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { useWorkOrderComplementItemsHistory } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { Group, InfiniteTable } from '@/components/_common';
import { ColumnsType } from 'antd/es/table/interface';
import { WorkOrderComplementItemHistory } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import WorkOrderItemTypeTag from '../WorkOrderItemList/WorkOrderItemTypeTag';
import { quantityFormatter } from '@/utils/number';
import { COLUMNS_WIDTH } from '@/utils/table';
import formatterPrice from '@/utils/number/formatterPrice';
import formatterPercent from '@/utils/number/formatterPercent';
import { Info } from 'react-feather';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import {
  WorkOrderItemOrigin,
  WorkOrderItemStockStatus,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useMemo, useState } from 'react';
import { useDisclosure } from '@/hooks';
import WorkOrderItemViewModal from '../WorkOrderItemViewModal';
import { WorkOrderItemTag } from '@/core/domain/workorder/WorkOrderItemTags/WorkOrderItemTags.types';
import EyeIcon from '@/components/_common/EyeIcon';

export type WorkOrderItemData = WorkOrderComplementItemHistory & {
  groupDiscountValue?: number;
  groupTotalValue?: number;
  groupStatus?: WorkOrderItemStockStatus[];
  disableDelete?: boolean;
  children?: (WorkOrderComplementItemHistory & { disableDelete?: boolean })[];
};

export default function WorkOrderAuthorizedItems() {
  const intl = useIntl();

  const [itemSelected, setItemSelected] = useState<WorkOrderItemData>();
  const [itemDetailOpened, itemDetailAction] = useDisclosure(false, {
    onClose: () => setItemSelected(undefined),
  });

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const itemsHistoryList = useWorkOrderComplementItemsHistory({
    workOrderId: workorder?.id,
    complementId: complement?.id,
  });

  const itemsHistory =
    itemsHistoryList.data?.pages.flatMap((page) => page.data) || [];

  const tableData = useMemo(
    () =>
      itemsHistory?.reduce((acc, curr) => {
        if (curr.kitId && acc.some((item) => item.kitId === curr.kitId)) {
          return acc.map((item) => {
            if (item.kitId === curr.kitId) {
              return {
                ...item,
                children: [...(item.children || []), curr],
                quantity: item.quantity + curr.quantity,
                discount: item.discount + curr.discount,
                unitPrice: item.unitPrice + curr.unitPrice,
                groupDiscountValue:
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupDiscountValue || 0),
                groupTotalValue:
                  curr.quantity * curr.unitPrice -
                  curr.quantity * curr.unitPrice * curr.discount +
                  (item.groupTotalValue || 0),
                disableDelete: true,
              };
            }
            return item;
          });
        }

        if (curr.kitId) {
          return [
            ...acc,
            {
              ...curr,
              id: curr.kitId,
              itemDescription: String(curr.kitName),
              sku: '',
              type: WorkOrderItemType.Kit,
              groupDiscountValue:
                curr.quantity * curr.unitPrice * curr.discount,
              groupTotalValue:
                curr.quantity * curr.unitPrice -
                curr.quantity * curr.unitPrice * curr.discount,
              children: [{ ...curr, disableDelete: true }],
            },
          ];
        }

        return [...acc, curr];
      }, [] as WorkOrderItemData[]),
    [itemsHistory],
  );

  const columnsConfig = useConfigurationColumnsPart({
    internalCode: 'sku',
    isSorter: false,
    widthColumns: 100,
  });

  const columns: ColumnsType<WorkOrderItemData> = [
    columnsConfig,
    {
      dataIndex: 'type',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.type',
      }),
      width: 200,
      ellipsis: true,
      render: (_, item) => (
        <WorkOrderItemTypeTag
          workOrderItem={{ type: item.type, origin: item.origin }}
        />
      ),
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.description',
      }),
      width: 200,
      dataIndex: 'itemDescription',
      render: (value, item) => (
        <EllipsisHoverToggle
          style={
            item.type === WorkOrderItemType.Kit ? { fontWeight: 'bold' } : {}
          }
        >
          {value}
        </EllipsisHoverToggle>
      ),
    },
    {
      dataIndex: 'quantity',
      width: COLUMNS_WIDTH.extraSmall,
      ellipsis: true,
      title: intl.formatMessage({
        id: 'workOrders.domain.items.quantityTime',
      }),
      align: 'right',
      render: (value: number, item) => (
        <span
          style={
            item.type === WorkOrderItemType.Kit ? { fontWeight: 'bold' } : {}
          }
        >
          {value ? quantityFormatter(value) : '-'}
        </span>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.unitPrice',
      }),
      dataIndex: 'unitPrice',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      align: 'right',
      render: (value: number, item) => (
        <span
          style={
            item.type === WorkOrderItemType.Kit ? { fontWeight: 'bold' } : {}
          }
        >
          {formatterPrice(value)}
        </span>
      ),
    },
    {
      dataIndex: 'discount',
      title: intl.formatMessage({
        id: 'workOrders.domain.items.discount',
      }),
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      align: 'right',
      render: (value: number, item) => (
        <Group gap={4} position="right">
          <span
            style={
              item.type === WorkOrderItemType.Kit ? { fontWeight: 'bold' } : {}
            }
          >
            {formatterPercent(value)}
          </span>

          <Tooltip title={String(value * 100).replace('.', ',') + '%'}>
            <Info size={14} />
          </Tooltip>
        </Group>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.discountValue',
      }),
      align: 'right',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      render: (_, item) => (
        <span
          style={
            item.type === WorkOrderItemType.Kit ? { fontWeight: 'bold' } : {}
          }
        >
          {formatterPrice(
            item.type === WorkOrderItemType.Kit
              ? item.groupDiscountValue || 0
              : item.quantity * item.unitPrice * item.discount,
          )}
        </span>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'workOrders.domain.items.totalValue',
      }),
      align: 'right',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      render: (_, item) => (
        <span
          style={
            item.type === WorkOrderItemType.Kit ? { fontWeight: 'bold' } : {}
          }
        >
          {formatterPrice(
            item.type === WorkOrderItemType.Kit
              ? item.groupTotalValue || 0
              : item.quantity * item.unitPrice -
                  item.quantity * item.unitPrice * item.discount,
          )}
        </span>
      ),
    },
    {
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      width: 50,
      align: 'right',
      fixed: 'right',
      render: (_, item) => (
        <EyeIcon
          disabled={item.type === WorkOrderItemType.Kit}
          iconProps={{
            onClick: () => {
              setItemSelected(item);
              itemDetailAction.open();
            },
          }}
        />
      ),
    },
  ];

  return (
    <>
      <Collapse style={{ marginTop: 24 }}>
        <Collapse.Panel
          header={intl.formatMessage({
            id: 'workOrders.details.authorizedBudgetItems',
          })}
          key="authorizedBudgetItems"
          collapsible={itemsHistoryList.isLoading ? 'disabled' : undefined}
        >
          <InfiniteTable
            dataSource={tableData}
            columns={columns}
            rowKey={(row) => row.id}
            loading={itemsHistoryList.isLoading}
            isFetching={itemsHistoryList.isFetching}
            fetchNextPage={itemsHistoryList.fetchNextPage}
            hasNextPage={itemsHistoryList.hasNextPage}
          />
        </Collapse.Panel>
      </Collapse>

      <WorkOrderItemViewModal
        open={itemDetailOpened}
        onCancel={itemDetailAction.close}
        workorderItem={{
          ...itemSelected,
          id: itemSelected?.id as number,
          type: itemSelected?.type as WorkOrderItemType,
          manufacturingCode: itemSelected?.manufacturingCode as string,
          quantity: itemSelected?.quantity as number,
          discount: itemSelected?.discount as number,
          unitCost: itemSelected?.unitCost as number,
          unitPrice: itemSelected?.unitPrice as number,
          description: itemSelected?.itemDescription as string,
          isInternal: itemSelected?.isInternal as boolean,
          isPaintedPart: itemSelected?.isPaintedPart as boolean,
          tag: itemSelected?.tag as Omit<WorkOrderItemTag, 'description'> & {
            name: string;
          },
          origin: itemSelected?.origin as WorkOrderItemOrigin,
          itemId:
            itemSelected?.type === WorkOrderItemType.Part
              ? itemSelected.partId
              : itemSelected?.type === WorkOrderItemType.Service
              ? itemSelected.serviceId
              : 0,
          subArea: {
            executionOrder: 0,
            id: 0,
            name: itemSelected?.subAreaName || '',
          },
          manufacturer: { id: 0, name: itemSelected?.manufacturerName || '' },
          supplier: { id: 0, name: itemSelected?.supplierName || '' },
          haveCommission: false,
          paint: 0,
          change: 0,
          repair: 0,
          status: {} as WorkOrderItemStockStatus,
        }}
      />
    </>
  );
}
